import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { searchApi } from '@/services/search';
import {
  SearchOutcomeHit,
  SearchOutcomesParams,
  SearchOutcomesResponse,
} from '@/types/search';

export const SEARCH_OUTCOMES_QUERY_KEY = 'search-outcomes';

const DEFAULT_SCORE_FLOOR = 0.35;
const DEFAULT_DISPLAY_CAP = 9;

export interface UseSearchOutcomesOptions
  extends Omit<SearchOutcomesParams, 'queryText'> {
  queryText: string;
  enabled?: boolean;
  /** Client-side minimum score. Pass `null` to disable. */
  scoreFloor?: number | null;
  /** Client-side max results after score filter. Pass `null` to disable. */
  displayCap?: number | null;
}

const buildOutcomesParams = (
  options: UseSearchOutcomesOptions
): SearchOutcomesParams => {
  const trimmedQuery = options.queryText.trim();

  return {
    queryText: trimmedQuery,
    ...(options.limit != null ? { limit: options.limit } : {}),
    ...(options.typeId ? { typeId: options.typeId } : {}),
    ...(options.categoryId ? { categoryId: options.categoryId } : {}),
    ...(options.resourceId ? { resourceId: options.resourceId } : {}),
    ...(options.contactId ? { contactId: options.contactId } : {}),
    ...(options.siteKey ? { siteKey: options.siteKey } : {}),
    ...(options.status ? { status: options.status } : {}),
    ...(options.result ? { result: options.result } : {}),
    ...(options.startDate ? { startDate: options.startDate } : {}),
    ...(options.endDate ? { endDate: options.endDate } : {}),
  };
};

const filterHits = (
  hits: SearchOutcomeHit[] | undefined,
  scoreFloor: number | null,
  displayCap: number | null
): SearchOutcomeHit[] => {
  if (!hits?.length) return [];

  let next = hits;

  if (scoreFloor != null) {
    next = next.filter(
      hit => typeof hit.score === 'number' && hit.score >= scoreFloor
    );
  }

  if (displayCap != null) {
    next = next.slice(0, displayCap);
  }

  return next;
};

export const useSearchOutcomes = ({
  queryText,
  limit = 10,
  typeId,
  categoryId,
  resourceId,
  contactId,
  siteKey,
  status,
  result,
  startDate,
  endDate,
  enabled = true,
  scoreFloor = DEFAULT_SCORE_FLOOR,
  displayCap = DEFAULT_DISPLAY_CAP,
}: UseSearchOutcomesOptions) => {
  const params = buildOutcomesParams({
    queryText,
    limit,
    typeId,
    categoryId,
    resourceId,
    contactId,
    siteKey,
    status,
    result,
    startDate,
    endDate,
  });
  const isQueryReady = params.queryText.length >= 2;

  const query = useQuery<SearchOutcomesResponse | undefined>({
    queryKey: [SEARCH_OUTCOMES_QUERY_KEY, params],
    queryFn: ({ signal }) => searchApi.outcomes(params, signal),
    enabled: enabled && isQueryReady,
  });

  const hits = useMemo(
    () => filterHits(query.data?.hits, scoreFloor, displayCap),
    [query.data?.hits, scoreFloor, displayCap]
  );

  return {
    data: query.data,
    hits,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
};
